/**
 * API contract types shared by the web app. Source of truth for the wire
 * shape: ../../api/src/routes/*.ts handlers. Keep these in sync.
 */

/** DB-side staff role values. */
export type StaffRoleDb = "dc" | "oo" | "eo" | "operator" | "admin_hall";

/** Response of GET /api/me — see apps/api/src/routes/me.ts. */
export interface MeResponse {
  staffId: string;
  name: string;
  nickname: string;
  role: StaffRoleDb;
  roles: StaffRoleDb[];
  telegramId: number | null;
  customPerms: Record<string, unknown>;
  fullstack: boolean;
}

/** Standard success envelope for list endpoints (CONTRACTS §C2). */
export interface ApiList<T> {
  results: T[];
  success?: boolean;
}

/** Legacy alias — prefer `ApiList<T>`. */
export type ListResponse<T> = ApiList<T>;

/** Standard success envelope for mutations (CONTRACTS §C2). */
export interface ApiOk {
  ok: boolean;
  id?: string | number;
  [extra: string]: unknown;
}

/** Legacy alias — prefer `ApiOk`. */
export type OkResponse = ApiOk;

/** Один конфликт гейта бронирований — apps/api/src/utils/conflicts.ts. */
export interface ApiConflict {
  type: string;
  severity: "block" | "warning" | "info";
  msg: string;
}

/** Standard error envelope. */
export interface ApiError {
  error: string;
  /**
   * 409 гейта бронирований (create/move/restore): всегда ≥1 block;
   * warning/info приезжают «за компанию». Отсутствует у точного дубля
   * слота — такой 409 force НЕ продавливает (bookings.ts:118).
   */
  conflicts?: ApiConflict[];
}

export class ApiException extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError | null,
    message?: string,
  ) {
    super(message ?? body?.error ?? `HTTP ${status}`);
    this.name = "ApiException";
  }
}

/* ────────────────────────────────────────────────────────────────────────
 * Domain shapes — directly mirror what v3 routes return.
 * Field names are snake_case to match the wire format; we map to camelCase
 * at the hook boundary (see hooks/mapBooking.ts).
 * ────────────────────────────────────────────────────────────────────── */

/** Branch — physical location. */
export interface Branch {
  id: string;
  name: string;
  address?: string;
  active: number;
  hours_open?: string;
  hours_close?: string;
  shifting?: boolean | number;
  tea_cutoff?: string;
  tea_before?: boolean | number;
  tea_after?: boolean | number;
  /** 17.09: мастер-класс сдвигает следующие слоты на свою длительность (настройка филиала). */
  mc_shift?: boolean | number;
  tea_rooms?: number;
  night_cutoff?: string;
  /** Конец ночного окна (A24); NULL → DEFAULT_NIGHT_END из @ub/shared. */
  night_end?: string | null;
  night_surcharge?: number; /** 28.09: учебный пакет «Юниверситета» (UNI_PACK_IDS, '' — нет). */ uni_pack?: string;
}

/** Quest — a single quest room. */
export interface Quest {
  id: string;
  name: string;
  short_name: string;
  branch_id: string;
  duration: number;
  reset_time: number;
  operators: number;
  active: number;
  sort_order?: number;
  consumables_note?: string;
  name_1c?: string; mk_id?: string | null; // mk_id — ID в кабинете «Мир Квестов» (23.08), /api/mk/* принимает его наравне с id
  /**
   * A31: состав операторов строкой «ММЖН…» (нотация scoring.ts violatesGender:
   * М — парень, Ж — девушка, Н — любой). При записи crew_rule сервер derive'ит
   * operators = длина состава; '' в PUT снимает правило (NULL).
   */
  crew_rule?: string | null;
}

/** Справочник расходников (A33) — существующие consumables; глобальные строки: quest_id NULL. */
export interface Consumable {
  id: string;
  name: string;
  unit?: string;
  per_game?: number;
  quest_id?: string | null;
  stock?: number;
  cost?: number;
  min_stock?: number;
  rule?: string;
  /** Округлять расход вверх до кратного (02.09); 0/нет = без округления. */
  round_to?: number;
  active: number;
}

/** Норма расходника квеста/спецверсии (A33, wire /api/quest-consumables). */
export interface QuestConsumable {
  quest_id: string;
  /** null = норма базового квеста; id спецверсии = её override. */
  version_id: string | null;
  consumable_id: string;
  qty: number;
  /** 1 = «на игрока», 0 = «на игру». */
  per_player: number;
}

/**
 * Спецверсия квеста (A35, wire /api/quest-versions — staff-CRUD поверх
 * клиентской special_versions; active = видимость Unifans, staff не трогает).
 */
export interface QuestVersion {
  id: string;
  title: string;
  /** Базовый квест; null = не привязана (бронирование недоступно). */
  slot_quest_id: string | null;
  duration_min: number | null;
  reset_min: number | null;
  /** Состав операторов A31 («МЖН»); headcount = длина строки. */
  operators_comp: string | null;
  client_visible: number;
  base_players: number | null;
  base_price: number | null;
  price_per_extra: number | null;
  min_price?: number | null; // «Мин, ₽» прайса (PRC-1) — staff-поле, НЕ клиентское price_min
  sort_order?: number;
  active?: number;
  /** GET ?branch= прилагает данные базового квеста (LEFT JOIN). */
  base_branch_id?: string | null;
  base_short?: string | null;
  base_duration?: number | null;
  base_reset?: number | null;
}

/** Ресурс (справочник) — routes/resources.ts; bool-привязка к квесту (A33). */
export interface Resource {
  id: string;
  name: string;
  type?: string;
  capacity?: number;
  rule?: string | null;
  cost?: number;
  round_to?: number;
  active?: number;
}

/** Привязка ресурса к квесту — M:N quest_resources (routes/resources.ts). */
export interface QuestResource {
  quest_id: string;
  resource_id: string;
}

/** Цена квеста — quest_prices: base_price до threshold чел + extra_per_player. */
export interface QuestPrice {
  quest_id: string; base_price: number; threshold: number; extra_per_player: number; min_price?: number;
}

/** Цены доп. услуг (A38/A41+): сетевая mc_prices (GET/POST /api/prices/mc) +
    филиальный override (/api/service-prices) — строка существует только при
    override, NULL-поле = наследование сетевой цены. */
export interface McPrice {
  mc_id: string; base_price: number; threshold: number; extra_per_player: number; min_price?: number;
}
export interface ServiceBranchPrice {
  service_id: string; branch_id: string; base_price: number | null;
  threshold: number | null; extra_per_player: number | null; min_price: number | null;
}

/** Fixed time slot for a quest. */
export interface Slot {
  id: string | number;
  quest_id: string;
  time: string;
  active: number;
}

/**
 * Linked group — quests that share a physical zone.
 * Booking one auto-blocks the overlapping slots in the others.
 * v3 API returns `quests`; older clients expect `members`. We carry both.
 */
export interface LinkedGroup {
  id: string | number;
  name: string;
  branch_id: string;
  quests?: string[];
  members: string[];
}

/** Lead source — tenant-configurable (A8.6). */
export interface LeadSource {
  id: string | number;
  name: string;
  type?: string;
  active: number;
  sort_order?: number;
  is_special?: number;
}

/** Master class → «доп. услуга» (A38/A41, аддитивно): branch_id '' = общесетевая
    (сентинел; филиальные строки — legacy); operators_comp — состав A31 «МЖН». */
export interface MasterClass {
  id: string; name: string; branch_id: string; duration?: number; active: number;
  short_name?: string | null; operators_comp?: string | null; sort_order?: number;
}
/** M:N услуги — service_consumables / service_resources (PUT /bulk replace-set). */
export interface ServiceConsumable { service_id: string; consumable_id: string; qty: number; per_player: number }
export interface ServiceResource { service_id: string; resource_id: string }

/** Tea room. */
export interface TeaRoom {
  id: string;
  branch_id: string;
  name: string;
  price: number;
  capacity: number;
  sort_order?: number;
  active?: number;
}
